import { Fragment } from 'react';
import PropTypes from 'prop-types';

import compareProductType from '../../../../utils/types/compareProduct';

import Tooltip from '@JohnLewisPartnership/jl-design-system/components/tooltip/Tooltip';
import { EnergyRating } from '@JohnLewisPartnership/jl-design-system/components/energy-rating/EnergyRating';

import styles from './CompareTable.module.scss';

import { CompareProduct } from '../../../../utils/types/types';
import {
  energyRatingTitleWithTooltip,
  getFeaturesForAllProducts,
  hasAnyProductsWithEnergyRating,
  hasAnyProductsWithEnergyRatingLegacy,
  uniqueTitlesWithToolTips,
} from './compareTableHelpers';

const FEATURE_NOT_APPLICABLE = '-';

type CompareTableProps = {
  products: CompareProduct[];
  maxCompare: number;
  className: string;
  modalHeaderClassName: string;
};

type TableRowHeaderProps = {
  featureTitle: string;
  tooltip: string;
  modalHeaderClassName: string;
};

type TableRowItemProps = {
  isEnergyRatingValue: boolean;
  isEnergyRatingLegacyValue: boolean;
  featureValue: string;
};

const getFeatureContent = (
  featureValue: string,
  isEnergyRatingValue: boolean,
  isEnergyRatingLegacyValue: boolean
) => {
  if (
    (isEnergyRatingValue || isEnergyRatingLegacyValue) &&
    featureValue !== FEATURE_NOT_APPLICABLE
  ) {
    return (
      <EnergyRating
        className={styles.energyRating}
        rating={isEnergyRatingValue ? featureValue : ''}
        ratingLegacy={isEnergyRatingLegacyValue ? featureValue : ''}
      />
    );
  }
  return <p className={styles.value}>{featureValue}</p>;
};

export const TableRowHeader = ({
  featureTitle,
  tooltip,
  modalHeaderClassName,
}: TableRowHeaderProps) => (
  <div className={styles.rowHeader} data-testid="compare-table-row">
    <h5 className={styles.title}>{featureTitle}</h5>

    {tooltip !== null && (
      <Tooltip
        icon
        id={featureTitle}
        anchor={featureTitle.length > 50 ? 'right' : 'left'}
        modalHeaderClassName={modalHeaderClassName}
        ariaLabel={featureTitle}
        data-testid={`tooltip-${featureTitle.replace(/ /g, '-')}`}
      >
        {tooltip}
      </Tooltip>
    )}
  </div>
);

export const TableRowItem = ({
  featureValue,
  isEnergyRatingValue,
  isEnergyRatingLegacyValue,
}: TableRowItemProps) => (
  <div className={styles.feature} data-testid="compare-table-row-item">
    <div className={styles.productInfo}>
      {getFeatureContent(
        featureValue,
        isEnergyRatingValue,
        isEnergyRatingLegacyValue
      )}
    </div>
  </div>
);

export const CompareTable = ({
  products = [],
  maxCompare = 3,
  className = '',
  modalHeaderClassName = '',
}: CompareTableProps) => {
  const allProducts = products.slice(0, maxCompare);
  if (allProducts.length === 0) {
    return null;
  }

  const hasLegacyAndNonLegacyEnergyRatings =
    hasAnyProductsWithEnergyRating(allProducts) &&
    hasAnyProductsWithEnergyRatingLegacy(allProducts);

  // Returns CompareTooltip for non-legacy energy rating if this exists on any of the products.
  // The displayTitle is suffixed with the rating scale if hasLegacyAndNonLegacyEnergyRatings=true
  const energyRatingTitle = energyRatingTitleWithTooltip({
    allProducts,
    hasLegacyAndNonLegacyEnergyRatings,
    isEnergyRatingLegacy: false,
  });

  // Returns CompareTooltip for legacy energy rating if this exists on any of the products.
  // The displayTitle is suffixed with the rating scale if hasLegacyAndNonLegacyEnergyRatings=true
  const energyRatingLegacyTitle = energyRatingTitleWithTooltip({
    allProducts,
    hasLegacyAndNonLegacyEnergyRatings,
    isEnergyRatingLegacy: true,
  });

  const allFeaturesForAllProducts = getFeaturesForAllProducts(
    allProducts,
    hasLegacyAndNonLegacyEnergyRatings
  );

  const allUniqueTitlesWithTooltips = energyRatingTitle.concat(
    energyRatingLegacyTitle.concat(uniqueTitlesWithToolTips(allProducts))
  );

  return (
    <div className={className} data-testid="compare-table">
      {allUniqueTitlesWithTooltips.map((feature) => {
        return (
          <Fragment key={`${feature.title}-row`}>
            <TableRowHeader
              featureTitle={feature.displayTitle}
              tooltip={feature.tooltip}
              key={`${feature.displayTitle}-row-header`}
              modalHeaderClassName={modalHeaderClassName}
            />
            <div className={styles.tableRow}>
              {allFeaturesForAllProducts.map((productFeatures) => {
                // For each feature title, get the matching property for each product
                // and either display the value or a hyphen (meaning not applicable)
                const { productId, features } = productFeatures;
                const match = features.find(
                  (productFeature) =>
                    productFeature.displayTitle === feature.displayTitle
                );

                const featureValue = match
                  ? match.value
                  : FEATURE_NOT_APPLICABLE;

                return (
                  <TableRowItem
                    isEnergyRatingValue={feature.isEnergyRatingValue}
                    isEnergyRatingLegacyValue={
                      feature.isEnergyRatingLegacyValue
                    }
                    featureValue={featureValue}
                    key={`${feature.title}-${productId}`}
                  />
                );
              })}
            </div>
          </Fragment>
        );
      })}
    </div>
  );
};

CompareTable.propTypes = {
  products: PropTypes.arrayOf(compareProductType),
  maxCompare: PropTypes.number,
  className: PropTypes.string,
  modalHeaderClassName: PropTypes.string,
};
